import { ImageCarousel } from "@/common/components/ImageCarousel";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import ResumeItem from "@/common/components/ResumeItem";
import Skeleton from "@/common/components/Skeleton";
import { equipmentStatusTranslation } from "@/common/constants/equipmentStatus.constant";
import { formatDate, formatDateTime, formatDecimalUnit } from "@/common/utils/formatters";
import { maintenanceWorkflowStatus } from "@/features/privateCompanies/constants/maintenance/workflows/maintenanceWorkflowsStatus.constant";
import { MaintenaceWorkflowData } from "@/features/privateCompanies/types/maintenance/workflows/maintenanceWorkflows.type";
import { CarIcon } from "lucide-react";

interface MaintenaceWorkflowSidebarProps {
  workflow: MaintenaceWorkflowData | undefined;
}

const MaintenanceWorkflowSidebar = ({ workflow }: MaintenaceWorkflowSidebarProps) => {
  return (
    <div className="bg-white-100 min-h-56 lg:w-56 rounded-sm lg:min-h-max lg:h-max overflow-y-auto overflow-x-hidden thin-scroll">
      <div className="p-4 hidden lg:flex flex-col gap-2">
        <ResumeItem
          label="ID do Processo de manutenção"
          value={workflow?.id.split('-')[0].toUpperCase() || ''}
          isLoading={!workflow}
          labelClassName="text-xs"
          valueClassName="text-sm"
        />

        <ResumeItem
          label="Status"
          value={workflow?.status ? maintenanceWorkflowStatus[workflow?.status] : '--'}
          isLoading={!workflow}
          labelClassName="text-xs"
          valueClassName="text-sm"
        />
      </div>

      <div className="bg-white-100 rounded-lg flex lg:flex-col">
        <ImageCarousel
          isLoading={!workflow}
          files={workflow?.equipment?.photos.map((photo) => ({ id: photo.id, file: photo?.url })) || []}
          className="size-56 border-none"
          showRemoveButton={false}
          Placeholder={(
            <ImageWithPlaceholder
              alt={workflow?.equipment.field_for_display_value}
              placeholderIcon={CarIcon}
              className="size-56 rounded-lg bg-white-800"
              iconClassName="size-32 border-n"
            />
          )}
          showExpandButton
          showNavigationButtons
        />

        <div className="p-4 flex flex-col gap-2">
          <p className="text-sm font-bold">
            {workflow?.equipment?.field_for_display_value || <Skeleton className="w-32 h-5" />}
          </p>

          <p className="text-xs">
            {workflow?.equipment ? (
              <>
                {workflow?.equipment?.model?.brand || 'Não informado'} - {workflow?.equipment?.model?.display_name} - {workflow?.equipment?.model?.year}
              </>
            ) : <Skeleton className="w-40 h-4" />}
          </p>

          <div className="flex flex-wrap gap-4">
            <ResumeItem
              label="Status"
              value={workflow?.equipment?.status
                ? equipmentStatusTranslation[(workflow?.equipment?.status) as keyof typeof equipmentStatusTranslation]
                : '--'}
              isLoading={!workflow?.equipment}
              labelClassName="text-xs"
              valueClassName="text-sm"
              skeletonValueWidth={60}
            />

            <ResumeItem
              label="Filial"
              value={workflow?.equipment?.branch
                ? workflow?.equipment?.branch.display_name || '--'
                : '--'}
              isLoading={!workflow?.equipment}
              labelClassName="text-xs"
              valueClassName="text-sm"
              skeletonValueWidth={80}
            />
          </div>

          <ResumeItem
            label="Quilometragem"
            value={(workflow?.equipment_usage_declaration?.value || workflow?.equipment.last_usage_value) ? (
              <div className="flex flex-col">
                <p>
                  {formatDecimalUnit(
                    workflow?.equipment_usage_declaration?.value || workflow?.equipment.last_usage_value!,
                    workflow?.equipment.model?.category?.unit_for_consumption
                  )}
                </p>

                {workflow?.equipment_usage_declaration ? (
                  <p className="text-xs font-light italic">
                    {formatDateTime(new Date(workflow?.equipment_usage_declaration.created_at))}
                  </p>
                ) : workflow.equipment.last_usage_at && (
                  <p className="text-xs font-light italic">
                    {formatDateTime(new Date(workflow.equipment.last_usage_at))}
                  </p>
                )}
              </div>
            ) : '--'}
            isLoading={!workflow?.equipment}
            labelClassName="text-xs"
            valueClassName="text-sm"
          />

          <ResumeItem
            label="Data de cadastro"
            value={workflow?.equipment?.created_at
              ? formatDate(new Date(workflow?.equipment?.created_at)) || '--'
              : '--'}
            isLoading={!workflow?.equipment}
            labelClassName="text-xs"
            valueClassName="text-sm"
          />
        </div>
      </div>
    </div>
  );
}

export default MaintenanceWorkflowSidebar;
